import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SectionHeading from '@/components/feature/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { useSeo } from '@/hooks/useSeo';
import { squadMembers, squadRecords } from '@/mocks/squads';
import { testimonials } from '@/mocks/testimonials';

const toneStyles: Record<string, string> = {
  现场: 'bg-primary-100 text-primary-800',
  蹲点: 'bg-secondary-100 text-secondary-900',
  细节: 'bg-accent-100 text-accent-900',
  经验: 'bg-background-200 text-foreground-700',
};

const principles = [
  { icon: 'ri-eye-line', title: '只看公开的', text: '内容来自公开场合的可见信息，不碰私人渠道，也不做未经证实的推测。' },
  { icon: 'ri-time-line', title: '过程也算数', text: '排队多久、散场顺不顺畅，这些细节比结论更容易帮到后来的人。' },
  { icon: 'ri-edit-2-line', title: '错了就改', text: '记录有误会更正，当事人提出合理异议会下架，发了就不改不是我们的风格。' },
];

export default function SquadPage() {
  useSeo({
    title: '吃瓜小队 - 511群众蹲点记录与成员介绍',
    description:
      '吃瓜小队是 511群众的前线观察组，负责见面会、音乐节、漫展等公开活动的蹲点记录。这里介绍小队成员、记录原则与最新蹲点动态。',
    keywords: '511群众吃瓜小队,吃瓜小队成员,511群众蹲点记录,511群众组团围观,现场见闻整理',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '吃瓜小队 - 511群众',
        description: '介绍 511群众吃瓜小队的成员构成、记录原则与蹲点动态。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://511qunzhong.space/' },
          { '@type': 'ListItem', position: 2, name: '吃瓜小队', item: 'https://511qunzhong.space/squad' },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden bg-accent-500">
        <div className="absolute inset-0 seed-rule opacity-30"></div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <p className="text-xs md:text-sm tracking-[0.18em] uppercase text-accent-950/70">Our squad</p>
          <h1 className="mt-3 font-heading font-black text-3xl md:text-5xl text-accent-950 leading-tight">
            吃瓜小队
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-accent-950/80">
            他们带着折叠椅、保温杯和记录本，出现在各种公开活动的现场。不追热闹，只把看到的原原本本记下来。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 lg:px-12 pt-6">
        <Breadcrumbs items={[{ label: '吃瓜小队' }]} />
      </div>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <SectionHeading
          eyebrow="Members"
          title="小队成员"
          description="每个人都有自己固定的盯场方向，凑在一起就是一张覆盖比较全的现场网。"
        />
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {squadMembers.map((member, index) => (
            <Reveal key={member.id} delay={index * 80}>
              <article className="h-full flex flex-col rounded-lg overflow-hidden border border-background-200/80 bg-background-50">
                <div className="w-full h-44 bg-background-200">
                  <img
                    src={member.image}
                    alt={`吃瓜小队成员 ${member.name} 的插画头像`}
                    title={`${member.name} · 511群众吃瓜小队`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-col flex-1 p-4 md:p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading font-bold text-base text-foreground-950">{member.name}</h3>
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs">
                      <i className="ri-leaf-line"></i>
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-primary-700 font-medium">{member.role}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600 flex-1">{member.note}</p>
                  <p className="mt-4 pt-3 border-t border-background-200/70 text-xs text-foreground-500 flex items-center gap-1.5">
                    <i className="ri-focus-3-line"></i>
                    主盯：{member.focus}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
        <SectionHeading
          eyebrow="Field log"
          title="最近在盯什么"
          description="这是小队最近的蹲点动态，完整记录会陆续整理进围观前线。"
        />
        <Reveal>
          <ol className="mt-8 relative border-l border-background-300 pl-6 md:pl-8 space-y-7">
            {squadRecords.map((record) => (
              <li key={record.id} className="relative">
                <span className="absolute -left-[31px] md:-left-[39px] top-1.5 w-3.5 h-3.5 rounded-full bg-primary-500 ring-4 ring-background-100"></span>
                <div className="flex flex-wrap items-center gap-3 mb-1.5">
                  <time className="text-xs text-foreground-500 tracking-wider">{record.time}</time>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      toneStyles[record.tone] ?? 'bg-background-200 text-foreground-700'
                    }`}
                  >
                    {record.tone}
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                    <i className="ri-map-pin-line"></i>
                    {record.place}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950">
                  {record.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{record.text}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {principles.map((item, index) => (
            <Reveal key={item.title} delay={index * 90}>
              <div className="h-full rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 text-xl">
                  <i className={item.icon}></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-lg bg-foreground-950 p-6 md:p-10">
          <div className="max-w-xl">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white leading-snug">
              想一起蹲点，或推荐一条线索？
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed">
              我们欢迎常跑现场的朋友加入小队，也欢迎你把自己看到的情况告诉我们。{testimonials.length} 位群众的反馈，都在推着我们把它做细一点。
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/topic/variety-finale-scene"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              看一条蹲点记录
            </Link>
            <Link
              to="/frontline"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-white/10 border border-white/25 text-white font-medium hover:bg-white/20 transition-colors whitespace-nowrap"
            >
              <i className="ri-radar-line"></i>
              围观前线
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}